import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Eye, EyeOff, Trash2, Copy, ExternalLink, Plus,
  ChevronUp, ChevronDown, Loader2, Code2, Film, Music, Presentation,
  FileText, Paperclip, Link as LinkIcon, ListChecks, Minus, Gift,
  UploadCloud, X, FolderOpen,
} from "lucide-react";
import { apiGet, apiPost, apiPatch, apiDelete, api, API } from "../lib/api";

const A = {
  bg: "#F7F5FB", card: "#FFFFFF", primary: "#5A3FC0", primarySoft: "#EDE7FF",
  text: "#2A1F4A", muted: "#7B7595", border: "#E8E1FA",
  danger: "#DC2A48", success: "#1A8A53", warn: "#D97706", gold: "#B8862F",
};

const newId = () => `blk_${Math.random().toString(36).slice(2, 12)}`;

const BLOCK_DEFS = [
  { type: "html", label: "Testo / HTML", icon: Code2, data: { html: "" } },
  { type: "video", label: "Video", icon: Film, data: { mode: "url", url: "", iframe_html: "", asset: null } },
  { type: "audio", label: "Audio", icon: Music, data: { mode: "upload", url: "", asset: null } },
  { type: "slides", label: "Diapositive", icon: Presentation, data: { mode: "upload", url: "", asset: null } },
  { type: "pdf", label: "PDF", icon: FileText, data: { name: "", description: "", asset: null } },
  { type: "file", label: "File scaricabile", icon: Paperclip, data: { name: "", description: "", asset: null } },
  { type: "link", label: "Link esterno", icon: LinkIcon, data: { url: "", label: "", description: "" } },
  { type: "resources", label: "Materiali / Risorse", icon: FolderOpen, data: { items: [] } },
  { type: "quiz", label: "Quiz (correzione auto)", icon: ListChecks, data: { questions: [] } },
  { type: "separator", label: "Separatore", icon: Minus, data: {} },
];

const blockDef = (type) => BLOCK_DEFS.find((d) => d.type === type) || BLOCK_DEFS[0];

// ───────────────────────── Small UI helpers ─────────────────────────
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider font-medium mb-1"
            style={{ color: A.muted }}>{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-[#5A3FC0]/25";
const inputStyle = { borderColor: A.border, color: A.text, background: "white" };

function IconBtn({ icon: Icon, onClick, title, danger, testId, disabled }) {
  return (
    <button onClick={onClick} title={title} data-testid={testId} disabled={disabled}
            className="h-8 w-8 grid place-items-center rounded-lg transition-colors hover:bg-black/5 disabled:opacity-30"
            style={{ color: danger ? A.danger : A.muted }}>
      <Icon className="h-4 w-4" />
    </button>
  );
}

// ───────────────────────── Chunked asset uploader ─────────────────────────
export function AssetUploader({ lessonId, accept, asset, onChange, testId }) {
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);
  const CHUNK = 20 * 1024 * 1024;

  const upload = async (file) => {
    if (!file) return;
    setProgress(0);
    try {
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK));
      const init = await apiPost("/admin/vip/cms/assets/init", {
        lesson_id: lessonId, filename: file.name, total_size: file.size,
        total_chunks: totalChunks,
        content_type: file.type || "application/octet-stream",
      });
      const chunkSize = init.chunk_size || CHUNK;
      const chunks = Math.max(1, Math.ceil(file.size / chunkSize));
      for (let i = 0; i < chunks; i++) {
        const blob = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
        let ok = false;
        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
          try {
            await api.post(`/admin/vip/cms/assets/${init.upload_id}/chunk/${i}`, blob,
              { headers: { "Content-Type": "application/octet-stream" } });
            ok = true;
          } catch (err) {
            if (attempt === 2) throw err;
            await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          }
        }
        setProgress(Math.round(((i + 1) / chunks) * 95));
      }
      const done = await apiPost(`/admin/vip/cms/assets/${init.upload_id}/finalize`, {});
      setProgress(null);
      onChange(done);
      toast.success("File caricato");
    } catch (e) {
      setProgress(null);
      toast.error(e?.response?.data?.detail || "Errore di caricamento");
    }
  };

  if (asset?.asset_id) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
           style={{ background: "#F0FBF5", border: `1px solid #BFE6D2` }}
           data-testid={testId ? `${testId}-done` : undefined}>
        <FileText className="h-4 w-4 shrink-0" style={{ color: A.success }} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate" style={{ color: A.text }}>
            {asset.filename || asset.asset_id}
          </p>
          <p className="text-[11px]" style={{ color: A.muted }}>
            {asset.size_bytes ? `${(asset.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ""}
          </p>
        </div>
        <a href={`${API}/vip/cms/assets/${asset.asset_id}`} target="_blank" rel="noreferrer"
           className="text-xs underline" style={{ color: A.primary }}>Apri</a>
        <IconBtn icon={X} danger title="Rimuovi" onClick={() => onChange(null)}
                 testId={testId ? `${testId}-remove` : undefined} />
      </div>
    );
  }
  return (
    <div>
      <input ref={inputRef} type="file" accept={accept} className="hidden"
             data-testid={testId}
             onChange={(e) => upload(e.target.files?.[0])} />
      {progress !== null ? (
        <div className="px-3 py-2.5 rounded-lg" style={{ background: A.primarySoft }}>
          <div className="flex items-center gap-2 text-xs mb-1.5" style={{ color: A.primary }}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento… {progress}%
          </div>
          <div className="h-1.5 rounded-full bg-white overflow-hidden">
            <div className="h-full rounded-full transition-[width]"
                 style={{ width: `${progress}%`, background: A.primary }} />
          </div>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()}
                data-testid={testId ? `${testId}-btn` : undefined}
                className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 border-dashed text-sm transition-colors hover:bg-[#F4F0FE]"
                style={{ borderColor: A.border, color: A.muted }}>
          <UploadCloud className="h-4 w-4" /> Carica file
        </button>
      )}
    </div>
  );
}

// ───────────────────────── Per-type block editors ─────────────────────────
function HtmlEditor({ data, update, lessonId, blockId }) {
  const [preview, setPreview] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px]" style={{ color: A.muted }}>
          Incolla qui il tuo HTML — verrà renderizzato esattamente come lo vede lo studente.
        </p>
        <button onClick={() => setPreview((p) => !p)}
                data-testid={`html-preview-toggle-${blockId}`}
                className="text-xs font-medium px-2.5 py-1 rounded-md"
                style={{ background: A.primarySoft, color: A.primary }}>
          {preview ? "Editor" : "Anteprima"}
        </button>
      </div>
      {preview ? (
        <div className="cms-html rounded-lg p-4 max-h-[420px] overflow-auto"
             style={{ border: `1px solid ${A.border}`, background: "white" }}
             dangerouslySetInnerHTML={{ __html: data.html || "<p><em>(vuoto)</em></p>" }} />
      ) : (
        <textarea value={data.html || ""} rows={14}
                  data-testid={`html-textarea-${blockId}`}
                  onChange={(e) => update({ html: e.target.value })}
                  placeholder="<div>…</div>"
                  className={`${inputCls} font-mono text-xs leading-relaxed`}
                  style={inputStyle} />
      )}
    </div>
  );
}

function VideoEditor({ data, update, lessonId, blockId }) {
  const mode = data.mode || "url";
  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {[["url", "URL (YouTube/Vimeo)"], ["iframe", "Codice iframe"], ["upload", "File caricato"]].map(([m, lbl]) => (
          <button key={m} onClick={() => update({ mode: m })}
                  data-testid={`video-mode-${m}-${blockId}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{
                    background: mode === m ? A.primary : A.primarySoft,
                    color: mode === m ? "white" : A.primary,
                  }}>{lbl}</button>
        ))}
      </div>
      {mode === "url" ? (
        <Field label="URL del video">
          <input value={data.url || ""} onChange={(e) => update({ url: e.target.value })}
                 data-testid={`video-url-${blockId}`}
                 placeholder="https://www.youtube.com/watch?v=…"
                 className={inputCls} style={inputStyle} />
        </Field>
      ) : mode === "iframe" ? (
        <Field label="Codice iframe / embed">
          <textarea value={data.iframe_html || ""} rows={4}
                    data-testid={`video-iframe-${blockId}`}
                    onChange={(e) => update({ iframe_html: e.target.value })}
                    placeholder='<iframe src="…"></iframe>'
                    className={`${inputCls} font-mono text-xs`} style={inputStyle} />
        </Field>
      ) : (
        <AssetUploader lessonId={lessonId} accept="video/*" asset={data.asset}
                       onChange={(a) => update({ asset: a })}
                       testId={`video-upload-${blockId}`} />
      )}
    </div>
  );
}

function AudioEditor({ data, update, lessonId, blockId }) {
  const mode = data.mode || "upload";
  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {[["upload", "File caricato"], ["url", "URL esterno"]].map(([m, lbl]) => (
          <button key={m} onClick={() => update({ mode: m })}
                  className="px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{
                    background: mode === m ? A.primary : A.primarySoft,
                    color: mode === m ? "white" : A.primary,
                  }}>{lbl}</button>
        ))}
      </div>
      {mode === "upload" ? (
        <AssetUploader lessonId={lessonId} accept="audio/*" asset={data.asset}
                       onChange={(a) => update({ asset: a })}
                       testId={`audio-upload-${blockId}`} />
      ) : (
        <Field label="URL dell'audio (mp3)">
          <input value={data.url || ""} onChange={(e) => update({ url: e.target.value })}
                 className={inputCls} style={inputStyle} placeholder="https://…/audio.mp3" />
        </Field>
      )}
    </div>
  );
}

function SlidesEditor({ data, update, lessonId, blockId }) {
  const mode = data.mode || "upload";
  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {[["upload", "File caricato (PDF / PPTX)"], ["url", "URL embed (Slides/Canva)"]].map(([m, lbl]) => (
          <button key={m} onClick={() => update({ mode: m })}
                  className="px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{
                    background: mode === m ? A.primary : A.primarySoft,
                    color: mode === m ? "white" : A.primary,
                  }}>{lbl}</button>
        ))}
      </div>
      {mode === "upload" ? (
        <div className="space-y-2">
          <AssetUploader lessonId={lessonId}
                         accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                         asset={data.asset}
                         onChange={(a) => update({ asset: a })}
                         testId={`slides-upload-${blockId}`} />
          <p className="text-[11px]" style={{ color: A.muted }}>
            Formati accettati: <strong>PDF</strong> (visualizzazione diretta) e{" "}
            <strong>PPT / PPTX</strong> (visualizzati con il lettore Office online —
            consigliato sotto i 10 MB per un caricamento rapido).
          </p>
        </div>
      ) : (
        <Field label="URL della presentazione embebida">
          <input value={data.url || ""} onChange={(e) => update({ url: e.target.value })}
                 className={inputCls} style={inputStyle}
                 placeholder="https://docs.google.com/presentation/…/embed" />
        </Field>
      )}
      <div className="rounded-lg p-3 space-y-2"
           style={{ border: `1px dashed ${A.border}`, background: "#FBFAFF" }}>
        <p className="text-[11px] uppercase tracking-wider font-semibold"
           style={{ color: A.primary }}>
          Immagine di anteprima (opzionale)
        </p>
        <p className="text-[11px]" style={{ color: A.muted }}>
          Se la carichi, lo studente vedrà questa immagine al posto del lettore
          incorporato, con un pulsante per aprire le diapositive in un'altra finestra.
        </p>
        {data.preview_asset?.asset_id ? (
          <AssetUploader lessonId={lessonId} asset={data.preview_asset}
                         onChange={(a) => update({ preview_asset: a })}
                         testId={`slides-preview-${blockId}`} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-2 items-start">
            <AssetUploader lessonId={lessonId} accept="image/*" asset={null}
                           onChange={(a) => update({ preview_asset: a, preview_url: "" })}
                           testId={`slides-preview-${blockId}`} />
            <input value={data.preview_url || ""}
                   onChange={(e) => update({ preview_url: e.target.value })}
                   className={inputCls} style={inputStyle}
                   placeholder="…oppure URL di un'immagine" />
          </div>
        )}
        {data.preview_asset?.asset_id || data.preview_url ? (
          <img src={data.preview_asset?.asset_id
                      ? `${API}/vip/cms/assets/${data.preview_asset.asset_id}`
                      : data.preview_url}
               alt="Anteprima" className="w-full rounded-lg object-cover"
               style={{ maxHeight: 140 }} />
        ) : null}
      </div>
    </div>
  );
}

function FileEditor({ data, update, lessonId, blockId, accept }) {
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nome visibile">
          <input value={data.name || ""} onChange={(e) => update({ name: e.target.value })}
                 data-testid={`file-name-${blockId}`}
                 className={inputCls} style={inputStyle} placeholder="Guida rapida" />
        </Field>
        <Field label="Descrizione (opzionale)">
          <input value={data.description || ""} onChange={(e) => update({ description: e.target.value })}
                 className={inputCls} style={inputStyle} />
        </Field>
      </div>
      <AssetUploader lessonId={lessonId} accept={accept} asset={data.asset}
                     onChange={(a) => update({ asset: a })}
                     testId={`file-upload-${blockId}`} />
    </div>
  );
}

function LinkEditor({ data, update, blockId }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <Field label="URL">
        <input value={data.url || ""} onChange={(e) => update({ url: e.target.value })}
               data-testid={`link-url-${blockId}`}
               className={inputCls} style={inputStyle} placeholder="https://…" />
      </Field>
      <Field label="Etichetta">
        <input value={data.label || ""} onChange={(e) => update({ label: e.target.value })}
               className={inputCls} style={inputStyle} />
      </Field>
      <Field label="Descrizione">
        <input value={data.description || ""} onChange={(e) => update({ description: e.target.value })}
               className={inputCls} style={inputStyle} />
      </Field>
    </div>
  );
}

function ResourcesEditor({ data, update, lessonId, blockId }) {
  const items = data.items || [];
  const setItem = (i, patch) => {
    const next = items.map((it, j) => (j === i ? { ...it, ...patch } : it));
    update({ items: next });
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    update({ items: next });
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="rounded-lg p-3 space-y-2"
             style={{ border: `1px solid ${A.border}`, background: "#FBFAFF" }}>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold" style={{ color: A.primary }}>#{i + 1}</span>
            <div className="ml-auto flex">
              <IconBtn icon={ChevronUp} onClick={() => move(i, -1)} title="Su" />
              <IconBtn icon={ChevronDown} onClick={() => move(i, 1)} title="Giù" />
              <IconBtn icon={Trash2} danger title="Elimina"
                       onClick={() => update({ items: items.filter((_, j) => j !== i) })} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={it.name || ""} onChange={(e) => setItem(i, { name: e.target.value })}
                   className={inputCls} style={inputStyle} placeholder="Nome della risorsa" />
            <input value={it.description || ""} onChange={(e) => setItem(i, { description: e.target.value })}
                   className={inputCls} style={inputStyle} placeholder="Descrizione" />
          </div>
          {it.asset?.asset_id ? (
            <AssetUploader lessonId={lessonId} asset={it.asset}
                           onChange={(a) => setItem(i, { asset: a })} />
          ) : (
            <div className="grid sm:grid-cols-2 gap-2 items-start">
              <AssetUploader lessonId={lessonId} asset={null}
                             onChange={(a) => setItem(i, { asset: a, url: "" })}
                             testId={`resource-upload-${blockId}-${i}`} />
              <input value={it.url || ""} onChange={(e) => setItem(i, { url: e.target.value })}
                     className={inputCls} style={inputStyle} placeholder="…oppure URL esterno" />
            </div>
          )}
        </div>
      ))}
      <button onClick={() => update({ items: [...items, { name: "", description: "", url: "", asset: null }] })}
              data-testid={`resources-add-${blockId}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg"
              style={{ background: A.primarySoft, color: A.primary }}>
        <Plus className="h-3.5 w-3.5" /> Aggiungi risorsa
      </button>
    </div>
  );
}

function QuizEditor({ data, update, blockId }) {
  const questions = data.questions || [];
  const setQ = (i, patch) => {
    update({ questions: questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  };
  return (
    <div className="space-y-3">
      {questions.map((q, i) => (
        <div key={i} className="rounded-lg p-3 space-y-2"
             style={{ border: `1px solid ${A.border}`, background: "#FBFAFF" }}>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold" style={{ color: A.primary }}>Domanda {i + 1}</span>
            <div className="ml-auto">
              <IconBtn icon={Trash2} danger title="Elimina domanda"
                       onClick={() => update({ questions: questions.filter((_, j) => j !== i) })} />
            </div>
          </div>
          <textarea value={q.stem || ""} rows={2}
                    data-testid={`quiz-stem-${blockId}-${i}`}
                    onChange={(e) => setQ(i, { stem: e.target.value })}
                    className={inputCls} style={inputStyle} placeholder="Testo della domanda…" />
          {(q.options || []).map((opt, oi) => (
            <div key={oi} className="flex items-center gap-2">
              <button onClick={() => setQ(i, { correct_index: oi })}
                      title="Segna come corretta"
                      data-testid={`quiz-correct-${blockId}-${i}-${oi}`}
                      className="h-6 w-6 rounded-full grid place-items-center text-[11px] font-bold shrink-0"
                      style={{
                        background: q.correct_index === oi ? A.success : "white",
                        color: q.correct_index === oi ? "white" : A.muted,
                        border: `1.5px solid ${q.correct_index === oi ? A.success : A.border}`,
                      }}>
                {String.fromCharCode(65 + oi)}
              </button>
              <input value={opt}
                     onChange={(e) => setQ(i, { options: q.options.map((o, j) => (j === oi ? e.target.value : o)) })}
                     className={inputCls} style={inputStyle} placeholder={`Opzione ${String.fromCharCode(65 + oi)}`} />
              <IconBtn icon={X} danger title="Rimuovi opzione"
                       disabled={(q.options || []).length <= 2}
                       onClick={() => setQ(i, {
                         options: q.options.filter((_, j) => j !== oi),
                         correct_index: q.correct_index === oi ? 0
                           : q.correct_index > oi ? q.correct_index - 1 : q.correct_index,
                       })} />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <button onClick={() => setQ(i, { options: [...(q.options || []), ""] })}
                    className="text-xs font-medium px-2.5 py-1 rounded-md"
                    style={{ background: A.primarySoft, color: A.primary }}>
              + Opzione
            </button>
          </div>
          <input value={q.explanation || ""}
                 onChange={(e) => setQ(i, { explanation: e.target.value })}
                 className={inputCls} style={inputStyle} placeholder="Spiegazione (mostrata dopo la verifica)" />
        </div>
      ))}
      <button onClick={() => update({
                questions: [...questions, { stem: "", options: ["", "", "", ""], correct_index: 0, explanation: "" }],
              })}
              data-testid={`quiz-add-question-${blockId}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg"
              style={{ background: A.primarySoft, color: A.primary }}>
        <Plus className="h-3.5 w-3.5" /> Aggiungi domanda
      </button>
    </div>
  );
}

function BlockEditorBody({ block, update, lessonId }) {
  const props = { data: block.data || {}, update, lessonId, blockId: block.id };
  switch (block.type) {
    case "html": return <HtmlEditor {...props} />;
    case "video": return <VideoEditor {...props} />;
    case "audio": return <AudioEditor {...props} />;
    case "slides": return <SlidesEditor {...props} />;
    case "pdf": return <FileEditor {...props} accept="application/pdf" />;
    case "file": return <FileEditor {...props} accept="*/*" />;
    case "link": return <LinkEditor {...props} />;
    case "resources": return <ResourcesEditor {...props} />;
    case "quiz": return <QuizEditor {...props} />;
    case "separator":
      return <p className="text-xs" style={{ color: A.muted }}>Linea di separazione visiva tra le sezioni.</p>;
    default: return null;
  }
}

// ───────────────────────── Main builder page ─────────────────────────
export default function VIPAdminLessonBuilder() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [modules, setModules] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    apiGet(`/admin/vip/lessons/${lessonId}`).then(setLesson)
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
    apiGet("/admin/vip/modules").then(setModules).catch(() => {});
  }, [lessonId]);

  const set = (patch) => { setLesson((l) => ({ ...l, ...patch })); setDirty(true); };
  const blocks = lesson?.blocks || [];
  const setBlocks = (next) => set({ blocks: next });

  const updateBlock = (id, patch) =>
    setBlocks(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const updateBlockData = (id, patch) =>
    setBlocks(blocks.map((b) => (b.id === id ? { ...b, data: { ...(b.data || {}), ...patch } } : b)));
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    setBlocks(next);
  };
  const addBlock = (def) => {
    setBlocks([...blocks, {
      id: newId(), type: def.type, title: "",
      data: JSON.parse(JSON.stringify(def.data)),
    }]);
    setAddOpen(false);
  };
  const duplicateBlock = (i) => {
    const copy = JSON.parse(JSON.stringify(blocks[i]));
    copy.id = newId();
    const next = [...blocks];
    next.splice(i + 1, 0, copy);
    setBlocks(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiPatch(`/admin/vip/lessons/${lessonId}`, {
        title_it: lesson.title_it, title_es: lesson.title_it,
        subtitle: lesson.subtitle || "",
        lesson_number: lesson.lesson_number ?? null,
        summary_it: lesson.summary_it || "",
        cover_image_url: lesson.cover_image_url || "",
        module_id: lesson.module_id,
        blocks,
      });
      setDirty(false);
      toast.success("Lezione salvata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); save(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line
  }, [lesson]);

  const togglePublish = async () => {
    try {
      if (dirty) await save();
      await apiPost(`/admin/vip/lessons/${lessonId}/publish?published=${lesson.published === false}`, {});
      set({ published: lesson.published === false });
      setDirty(false);
      toast.success(lesson.published === false ? "Lezione pubblicata" : "Lezione in bozza");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const toggleFree = async () => {
    try {
      if (dirty) await save();
      const enabled = !lesson.is_free_preview;
      await apiPost(`/admin/vip/lessons/${lessonId}/free-preview`, { enabled });
      set({ is_free_preview: enabled });
      setDirty(false);
      toast.success(enabled ? "Anteprima gratuita attivata" : "Anteprima gratuita disattivata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const duplicateLesson = async () => {
    try {
      const copy = await apiPost(`/admin/vip/cms/lessons/${lessonId}/duplicate`, {});
      toast.success("Lezione duplicata");
      navigate(`/admin/vip/lessons/${copy.lesson_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const deleteLesson = async () => {
    try {
      await apiDelete(`/admin/vip/lessons/${lessonId}`);
      toast.success("Lezione eliminata");
      navigate("/admin/vip/lessons");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  if (!lesson) {
    return (
      <div className="min-h-[50vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: A.primary }} />
      </div>
    );
  }

  return (
    <div data-testid="lesson-builder" className="pb-24">
      {/* Top bar */}
      <div className="sticky top-0 z-30 -mx-4 sm:-mx-8 px-4 sm:px-8 py-3 mb-6 flex items-center gap-3 flex-wrap"
           style={{ background: "rgba(247,245,251,0.94)", backdropFilter: "blur(8px)",
                    borderBottom: `1px solid ${A.border}` }}>
        <button onClick={() => navigate("/admin/vip/lessons")}
                data-testid="builder-back"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg"
                style={{ color: A.muted }}>
          <ArrowLeft className="h-4 w-4" /> Lezioni
        </button>
        <p className="font-serif text-lg truncate flex-1 min-w-[120px]" style={{ color: A.text }}>
          {lesson.title_it || "Nuova lezione"}
        </p>
        {dirty ? (
          <span className="text-[11px] px-2.5 py-1 rounded-full font-medium"
                style={{ background: "#FEF3E2", color: A.warn }}>
            Modifiche non salvate
          </span>
        ) : null}
        <a href={`/vip/lessons/${lessonId}`} target="_blank" rel="noreferrer"
           data-testid="builder-preview"
           className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg"
           style={{ background: "white", color: A.primary, border: `1px solid ${A.border}` }}>
          <ExternalLink className="h-3.5 w-3.5" /> Anteprima
        </a>
        <button onClick={save} disabled={saving} data-testid="builder-save"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60"
                style={{ background: A.primary }}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Salva
        </button>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
        {/* Meta sidebar */}
        <aside className="rounded-2xl p-4 space-y-4 lg:sticky lg:top-20"
               style={{ background: A.card, border: `1px solid ${A.border}` }}>
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: A.primary }}>
            Informazioni della lezione
          </p>
          <Field label="Titolo">
            <input value={lesson.title_it || ""} onChange={(e) => set({ title_it: e.target.value })}
                   data-testid="builder-title" className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Sottotitolo">
            <input value={lesson.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })}
                   data-testid="builder-subtitle" className={inputCls} style={inputStyle} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="N° lezione">
              <input value={lesson.lesson_number ?? ""} onChange={(e) => set({ lesson_number: e.target.value })}
                     data-testid="builder-number" className={inputCls} style={inputStyle} />
            </Field>
            <Field label="Modulo">
              <select value={lesson.module_id || ""} onChange={(e) => set({ module_id: e.target.value })}
                      data-testid="builder-module" className={inputCls} style={inputStyle}>
                {modules.map((m) => (
                  <option key={m.module_id} value={m.module_id}>{m.title_it}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Descrizione breve">
            <textarea value={lesson.summary_it || ""} rows={3}
                      data-testid="builder-summary"
                      onChange={(e) => set({ summary_it: e.target.value })}
                      className={inputCls} style={inputStyle} />
          </Field>
          <Field label="Immagine di copertina (URL)">
            <input value={lesson.cover_image_url || ""} onChange={(e) => set({ cover_image_url: e.target.value })}
                   data-testid="builder-cover" className={inputCls} style={inputStyle}
                   placeholder="https://…/copertina.jpg" />
          </Field>
          {lesson.cover_image_url ? (
            <img src={lesson.cover_image_url} alt="" className="w-full rounded-lg object-cover" style={{ maxHeight: 120 }} />
          ) : null}

          <div className="pt-2 space-y-2" style={{ borderTop: `1px solid ${A.border}` }}>
            <button onClick={togglePublish} data-testid="builder-publish"
                    className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium px-3 py-2.5 rounded-lg"
                    style={{
                      background: lesson.published === false ? A.success : "#FEF3E2",
                      color: lesson.published === false ? "white" : A.warn,
                    }}>
              {lesson.published === false ? <><Eye className="h-4 w-4" /> Pubblica</> : <><EyeOff className="h-4 w-4" /> Metti in bozza</>}
            </button>
            <button onClick={toggleFree} data-testid="builder-free-preview"
                    className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium px-3 py-2.5 rounded-lg"
                    style={{
                      background: lesson.is_free_preview ? "#FBF3D9" : "white",
                      color: lesson.is_free_preview ? A.gold : A.muted,
                      border: `1px solid ${lesson.is_free_preview ? "#EAD9A8" : A.border}`,
                    }}>
              <Gift className="h-4 w-4" />
              {lesson.is_free_preview ? "Anteprima gratuita: ATTIVA" : "Anteprima gratuita: no"}
            </button>
            <button onClick={duplicateLesson} data-testid="builder-duplicate"
                    className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium px-3 py-2.5 rounded-lg"
                    style={{ background: "white", color: A.primary, border: `1px solid ${A.border}` }}>
              <Copy className="h-4 w-4" /> Duplica lezione
            </button>
            {confirmDelete ? (
              <div className="flex gap-2">
                <button onClick={deleteLesson} data-testid="builder-delete-confirm"
                        className="flex-1 text-sm font-semibold px-3 py-2.5 rounded-lg text-white"
                        style={{ background: A.danger }}>
                  Conferma eliminazione
                </button>
                <button onClick={() => setConfirmDelete(false)}
                        className="px-3 py-2.5 rounded-lg text-sm"
                        style={{ border: `1px solid ${A.border}`, color: A.muted }}>
                  Annulla
                </button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} data-testid="builder-delete"
                      className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium px-3 py-2.5 rounded-lg"
                      style={{ background: "white", color: A.danger, border: `1px solid ${A.border}` }}>
                <Trash2 className="h-4 w-4" /> Elimina lezione
              </button>
            )}
          </div>
        </aside>

        {/* Blocks */}
        <main className="space-y-4">
          {blocks.length === 0 ? (
            <div className="rounded-2xl p-10 text-center"
                 style={{ background: A.card, border: `2px dashed ${A.border}` }}>
              <Plus className="h-8 w-8 mx-auto mb-3" style={{ color: A.primary }} />
              <p className="text-sm mb-1" style={{ color: A.text }}>Questa lezione è ancora vuota.</p>
              <p className="text-xs" style={{ color: A.muted }}>
                Aggiungi il primo blocco: HTML, video, audio, diapositive, PDF, quiz…
              </p>
            </div>
          ) : null}

          {blocks.map((b, i) => {
            const def = blockDef(b.type);
            const Icon = def.icon;
            return (
              <div key={b.id} data-testid={`builder-block-${b.id}`}
                   className="rounded-2xl overflow-hidden"
                   style={{ background: A.card, border: `1px solid ${A.border}` }}>
                <div className="flex items-center gap-2 px-4 py-2.5"
                     style={{ background: "#FBFAFF", borderBottom: `1px solid ${A.border}` }}>
                  <span className="h-7 w-7 rounded-lg grid place-items-center shrink-0"
                        style={{ background: A.primarySoft }}>
                    <Icon className="h-3.5 w-3.5" style={{ color: A.primary }} />
                  </span>
                  <span className="text-[11px] uppercase tracking-wider font-semibold shrink-0"
                        style={{ color: A.primary }}>{def.label}</span>
                  {b.type !== "separator" ? (
                    <input value={b.title || ""}
                           onChange={(e) => updateBlock(b.id, { title: e.target.value })}
                           placeholder="Titolo della sezione (opzionale)"
                           className="flex-1 min-w-[100px] px-2 py-1 text-sm bg-transparent outline-none"
                           style={{ color: A.text }} />
                  ) : <span className="flex-1" />}
                  <div className="flex shrink-0">
                    <IconBtn icon={ChevronUp} onClick={() => moveBlock(i, -1)} title="Sposta su"
                             disabled={i === 0} testId={`block-up-${b.id}`} />
                    <IconBtn icon={ChevronDown} onClick={() => moveBlock(i, 1)} title="Sposta giù"
                             disabled={i === blocks.length - 1} testId={`block-down-${b.id}`} />
                    <IconBtn icon={Copy} onClick={() => duplicateBlock(i)} title="Duplica" />
                    <IconBtn icon={Trash2} danger title="Elimina blocco"
                             testId={`block-delete-${b.id}`}
                             onClick={() => setBlocks(blocks.filter((x) => x.id !== b.id))} />
                  </div>
                </div>
                <div className="p-4">
                  <BlockEditorBody block={b} lessonId={lessonId}
                                   update={(patch) => updateBlockData(b.id, patch)} />
                </div>
              </div>
            );
          })}

          {/* Add block */}
          <div className="rounded-2xl p-4" style={{ background: A.card, border: `1px solid ${A.border}` }}>
            {addOpen ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold" style={{ color: A.text }}>Scegli il tipo di blocco</p>
                  <IconBtn icon={X} onClick={() => setAddOpen(false)} title="Chiudi" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {BLOCK_DEFS.map((def) => {
                    const Icon = def.icon;
                    return (
                      <button key={def.type} onClick={() => addBlock(def)}
                              data-testid={`add-block-${def.type}`}
                              className="flex flex-col items-center gap-2 p-3 rounded-xl text-xs font-medium transition-colors hover:bg-[#F4F0FE]"
                              style={{ border: `1px solid ${A.border}`, color: A.text }}>
                        <Icon className="h-5 w-5" style={{ color: A.primary }} />
                        {def.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <button onClick={() => setAddOpen(true)} data-testid="builder-add-block"
                      className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white"
                      style={{ background: A.primary }}>
                <Plus className="h-4 w-4" /> Aggiungi blocco
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
